package dairy.mynotes.repository

import dairy.mynotes.firebase.FirebaseService
import dairy.mynotes.utils.encryptNote
import dairy.mynotes.utils.decryptNote
import kotlinx.serialization.Serializable

/**
 * Data class representing a Note
 */
@Serializable
data class Note(
    val id: String = "",
    val title: String = "",
    val content: String = "",
    val category: String = "",
    val userId: String = "",
    val createdAt: Long = 0,
    val updatedAt: Long = 0
)

// Helper to safely read a document field as String
private fun readString(doc: dev.gitlive.firebase.firestore.DocumentSnapshot, field: String): String {
    return try {
        doc.get<String?>(field) ?: ""
    } catch (e: Exception) {
        try { doc.get<Any?>(field)?.toString() ?: "" } catch (_: Exception) { "" }
    }
}

// Helper to safely read a document field as Long
private fun readLong(doc: dev.gitlive.firebase.firestore.DocumentSnapshot, field: String): Long {
    return try { doc.get<Long>(field) ?: 0L } catch (e: Exception) { 0L }
}

// Helper to map a document to a Note (decrypts content)
private fun documentToNote(doc: dev.gitlive.firebase.firestore.DocumentSnapshot): Note {
    val userId = readString(doc, "userId")
    val rawContent = readString(doc, "content")
    val decryptedContent = decryptNote(rawContent, userId)
    return Note(
        id = doc.id,
        title = readString(doc, "title"),
        content = decryptedContent,
        category = readString(doc, "category"),
        userId = userId,
        createdAt = readLong(doc, "createdAt"),
        updatedAt = readLong(doc, "updatedAt")
    )
}

/**
 * Repository for Firestore Notes operations
 * Handles CRUD operations for notes
 */
class NotesRepository {
    private val firestore get() = FirebaseService.firestore
    private val notesCollection get() = firestore.collection("notes")

    /**
     * Save a new note or update existing note
     * @return Result with document ID on success or Exception on failure
     */
    suspend fun saveNote(note: Note): Result<String> {
        return try {
            val timestamp = currentTimeMillis()
            val docRef = if (note.id.isEmpty()) {
                notesCollection.document
            } else {
                notesCollection.document(note.id)
            }
            
            // Encrypt content before storing
            val encryptedContent = encryptNote(note.content, note.userId)
            // Convert Note to map for Firestore
            // Note: title is kept empty as we only collect category and description
            val noteData = mapOf(
                "id" to docRef.id,
                "title" to "",
                "content" to encryptedContent,
                "category" to note.category,
                "userId" to note.userId,
                "createdAt" to (if (note.createdAt == 0L) timestamp else note.createdAt).toFirestoreValue(),
                "updatedAt" to timestamp.toFirestoreValue()
            )
            
            docRef.set(noteData)
            Result.success(docRef.id)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Get all notes for a specific user
     * @return Result with list of notes
     */
    suspend fun getUserNotes(userId: String): Result<List<Note>> {
        return try {
            val snapshot = notesCollection
                .where { "userId" equalTo userId }
                .get()

            val notes = snapshot.documents
                .map { documentToNote(it) }
                .sortedByDescending { it.updatedAt }
            Result.success(notes)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Get notes by category for a specific user
     * @return Result with list of notes
     */
    suspend fun getUserNotesByCategory(userId: String, category: String): Result<List<Note>> {
        return try {
            val snapshot = notesCollection
                .where {
                    "userId" equalTo userId
                    "category" equalTo category
                }
                .get()

            val notes = snapshot.documents
                .map { documentToNote(it) }
                .sortedByDescending { it.updatedAt }
            Result.success(notes)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Get a single note by ID
     * @return Result with Note on success or Exception on failure
     */
    suspend fun getNote(noteId: String): Result<Note?> {
        return try {
            val doc = notesCollection.document(noteId).get()
            if (doc.exists) {
                Result.success(documentToNote(doc))
            } else {
                Result.success(null)
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Delete a note
     * @return Result with Unit on success or Exception on failure
     */
    suspend fun deleteNote(noteId: String): Result<Unit> {
        return try {
            notesCollection.document(noteId).delete()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Update only the content of an existing note (does NOT touch userId or other fields)
     * @return Result with Unit on success or Exception on failure
     */
    suspend fun updateNoteContent(noteId: String, content: String, userId: String = ""): Result<Unit> {
        return try {
            val timestamp = currentTimeMillis()
            val encryptedContent = if (userId.isNotEmpty()) encryptNote(content, userId) else content
            notesCollection.document(noteId).update(
                "content" to encryptedContent,
                "updatedAt" to timestamp.toFirestoreValue()
            )
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Update note category
     * @return Result with Unit on success or Exception on failure
     */
    suspend fun updateNoteCategory(noteId: String, category: String): Result<Unit> {
        return try {
            val timestamp = currentTimeMillis()
            notesCollection.document(noteId).update(
                "category" to category,
                "updatedAt" to timestamp.toFirestoreValue()
            )
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Delete all notes for a user in a specific category
     * @return Result with Unit on success or Exception on failure
     */
    suspend fun deleteNotesByCategory(userId: String, category: String): Result<Unit> {
        return try {
            val snapshot = notesCollection
                .where {
                    "userId" equalTo userId
                    "category" equalTo category
                }
                .get()
            snapshot.documents.forEach { doc ->
                notesCollection.document(doc.id).delete()
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Delete all notes for a user
     * @return Result with Unit on success or Exception on failure
     */
    suspend fun deleteAllUserNotes(userId: String): Result<Unit> {
        return try {
            val snapshot = notesCollection
                .where { "userId" equalTo userId }
                .get()
            
            snapshot.documents.forEach { doc ->
                notesCollection.document(doc.id).delete()
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}

// Platform-specific function to get current time in milliseconds
expect fun currentTimeMillis(): Long

// Platform-specific conversion: JS Firestore requires Double for numeric fields,
// Android/iOS accept Long directly.
expect fun Long.toFirestoreValue(): Any

